import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import React from 'react';
import { createAndFireEvent, withAnalyticsContext, withAnalyticsEvents } from '@atlaskit/analytics-next';
import { ASC, DESC, LARGE, SMALL } from '../internal/constants';
import { assertIsSortable, getPageRows, validateSortKey } from '../internal/helpers';
import { Caption, PaginationWrapper, Table } from '../styled/DynamicTable';
import { EmptyViewContainer, EmptyViewWithFixedHeight } from '../styled/EmptyBody';
import Body from './Body';
import LoadingContainer from './LoadingContainer';
import LoadingContainerAdvanced from './LoadingContainerAdvanced';
import ManagedPagination from './managed-pagination';
import RankableTableBody from './rankable/Body';
import TableHead from './TableHead';
const packageName = "@atlaskit/dynamic-table";
const packageVersion = "14.4.3";

function toggleSortOrder(currentSortOrder) {
  switch (currentSortOrder) {
    case DESC:
      return ASC;

    case ASC:
      return DESC;

    default:
      return currentSortOrder;
  }
}

class DynamicTable extends React.Component {
  constructor(...args) {
    super(...args);

    _defineProperty(this, "state", {
      isRanking: false
    });

    _defineProperty(this, "onSort", item => () => {
      const {
        sortKey,
        sortOrder,
        onSort,
        isRankable
      } = this.props;
      const {
        key
      } = item;

      if (!key) {
        return;
      }

      if (onSort && isRankable && key === sortKey && sortOrder === DESC) {
        onSort({
          key: null,
          sortOrder: null,
          item
        });
        return;
      }

      const sortOrderFormatted = key !== sortKey ? ASC : toggleSortOrder(sortOrder);

      if (onSort) {
        onSort({
          key,
          item,
          sortOrder: sortOrderFormatted
        });
      }
    });

    _defineProperty(this, "onSetPage", (page, event) => {
      const {
        onSetPage
      } = this.props;

      if (onSetPage) {
        onSetPage(page, event);
      }
    });

    _defineProperty(this, "onRankStart", params => {
      this.setState({
        isRanking: true
      });

      if (this.props.onRankStart) {
        this.props.onRankStart(params);
      }
    });

    _defineProperty(this, "onRankEnd", params => {
      this.setState({
        isRanking: false
      });

      if (this.props.onRankEnd) {
        this.props.onRankEnd(params);
      }
    });

    _defineProperty(this, "getSpinnerSize", () => {
      const {
        page,
        rows,
        rowsPerPage,
        loadingSpinnerSize
      } = this.props;

      if (loadingSpinnerSize) {
        return loadingSpinnerSize;
      }

      return getPageRows(rows || [], page, rowsPerPage).length > 2 ? LARGE : SMALL;
    });

    _defineProperty(this, "renderEmptyBody", () => {
      const {
        emptyView,
        isLoading
      } = this.props;

      if (isLoading) {
        return /*#__PURE__*/React.createElement(EmptyViewWithFixedHeight, null);
      }

      return emptyView && /*#__PURE__*/React.createElement(EmptyViewContainer, null, emptyView);
    });
  }

  UNSAFE_componentWillMount() {
    validateSortKey(this.props.sortKey, this.props.head);
    assertIsSortable(this.props.head);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.sortKey !== nextProps.sortKey || this.props.head !== nextProps.head) {
      validateSortKey(nextProps.sortKey, nextProps.head);
    }

    if (this.props.head !== nextProps.head) {
      assertIsSortable(nextProps.head);
    }
  }

  render() {
    const {
      caption,
      head,
      highlightedRowIndex,
      isFixedSize,
      page,
      rows,
      rowsPerPage,
      sortKey,
      sortOrder,
      isLoading,
      isRankable,
      isRankingDisabled,
      paginationi18n,
      onPageRowsUpdate,
      testId,
      totalRows: passedDownTotalRows,
      label
    } = this.props;
    const rowsLength = rows && rows.length;
    let totalPages; // set a flag to denote the dynamic table might get only one page of data
    // for paginated data

    let isTotalPagesControlledExternally = false;

    if (passedDownTotalRows && Number.isInteger(passedDownTotalRows) && rowsPerPage && rowsLength && rowsLength <= passedDownTotalRows) {
      /**
       * If total number of rows / records have been passed down as prop
       * Then table is being fed paginated data from server or other sources
       * In this case, we want to respect information passed down by server or external source
       * Rather than relying on our computation based on number of rows
       */
      totalPages = Math.ceil(passedDownTotalRows / rowsPerPage);
      isTotalPagesControlledExternally = true;
    } else {
      totalPages = rowsLength && rowsPerPage ? Math.ceil(rowsLength / rowsPerPage) : 0;
    }

    const bodyProps = {
      highlightedRowIndex,
      rows,
      head,
      sortKey,
      sortOrder,
      rowsPerPage,
      page,
      isFixedSize: isFixedSize || false,
      onPageRowsUpdate,
      isTotalPagesControlledExternally,
      ref: el => {
        this.tableBody = el;
      },
      testId
    };
    const rowsExist = !!rowsLength;
    const spinnerSize = this.getSpinnerSize();
    const emptyBody = this.renderEmptyBody();
    const canRank = isRankable && !sortKey;
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(LoadingContainerAdvanced, {
      isLoading: isLoading && rowsExist,
      spinnerSize: spinnerSize,
      targetRef: () => this.tableBody,
      testId: testId
    }, /*#__PURE__*/React.createElement(Table, {
      isFixedSize: isFixedSize,
      "data-testid": testId && `${testId}--table`,
      "aria-label": label
    }, !!caption && /*#__PURE__*/React.createElement(Caption, null, caption), head && /*#__PURE__*/React.createElement(TableHead, {
      head: head,
      onSort: this.onSort,
      sortKey: sortKey,
      sortOrder: sortOrder,
      isRanking: this.state.isRanking,
      isRankable: canRank,
      testId: testId
    }), rowsExist && (canRank ? /*#__PURE__*/React.createElement(RankableTableBody, _extends({}, bodyProps, {
      isRanking: this.state.isRanking,
      onRankStart: this.onRankStart,
      onRankEnd: this.onRankEnd,
      isRankingDisabled: isRankingDisabled || isLoading || false
    })) : /*#__PURE__*/React.createElement(Body, bodyProps)))), !totalPages ? null : /*#__PURE__*/React.createElement(PaginationWrapper, null, /*#__PURE__*/React.createElement(ManagedPagination, {
      value: page,
      onChange: this.onSetPage,
      total: totalPages,
      i18n: paginationi18n
    })), !rowsExist && emptyBody && /*#__PURE__*/React.createElement(LoadingContainer, {
      isLoading: isLoading,
      spinnerSize: LARGE,
      testId: testId
    }, emptyBody));
  }

}

_defineProperty(DynamicTable, "defaultProps", {
  isLoading: false,
  isFixedSize: false,
  rowsPerPage: Infinity,
  onSetPage: () => {},
  onSort: () => {},
  page: 1,
  isRankable: false,
  isRankingDisabled: false,
  onRankStart: () => {},
  onRankEnd: () => {},
  paginationi18n: {
    prev: 'Prev',
    next: 'Next',
    label: 'Pagination'
  }
});

export { DynamicTable as DynamicTableWithoutAnalytics };
const createAndFireEventOnAtlaskit = createAndFireEvent('atlaskit');
export default withAnalyticsContext({
  componentName: 'dynamicTable',
  packageName,
  packageVersion
})(withAnalyticsEvents({
  onSort: createAndFireEventOnAtlaskit({
    action: 'sorted',
    actionSubject: 'dynamicTable',
    attributes: {
      componentName: 'dynamicTable',
      packageName,
      packageVersion
    }
  }),
  onRankEnd: createAndFireEventOnAtlaskit({
    action: 'ranked',
    actionSubject: 'dynamicTable',
    attributes: {
      componentName: 'dynamicTable',
      packageName,
      packageVersion
    }
  })
})(DynamicTable));